// client/src/components/map/assetMarker.tsx
//
// The LexiStar asset marker (ST-00244, option C "puck", chosen 2026-10-19): a disc in the
// state colour with an upright side-view truck or loader pictogram, and — while the asset is
// moving — a pointed nose that turns to the heading. When it stops the nose retracts and it is
// a plain disc; with no live fix it is a hollow dashed disc (LS-09 §4). So moving, stopped and
// stale differ in SHAPE, not only colour, and the pictogram always stays upright and readable.
// The pictogram faces the way the asset travels (right for headings 0–180°, left for 180–360°;
// the maps are not rotated).
//
// Legibility on any basemap: a white keyline (reads on dark satellite imagery), a soft
// two-layer drop shadow (lifts it off the light street style), and a pictogram in white or ink,
// whichever holds 3:1 on the state colour. Map content does not follow the app theme (the
// basemaps do not), so these are literal colours, not theme tokens.
//
// One description, three renderers:
//  - <AssetMarkerIcon>  — inline SVG (Replay's react-map-gl <Marker>, the control-room chips);
//  - assetMarkerSvg()   — standalone SVG markup of the whole icon or of one map layer part;
//  - map/assetMarkerImages — the parts as maplibre symbol-layer images: the nose (turned by
//    `icon-rotate`), the face (disc + pictogram, never turned) and the shadow.

import { useId } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { ASSET_STATUS, type AssetStatusKey } from './assetStatus';

export type AssetMarkerKind = 'truck' | 'loader';

/** Anything that says what class of asset this is (asset row, replay asset, live vehicle). */
export interface AssetClassHints {
  asset_type?: string | null;
  vehicle_class?: string | null;
  vehicleClass?: string | null;
}

/** 'loader' when any hint names a loader (asset_type 'loader', vehicle_class 'wheel loader' …). */
export function assetMarkerKind(
  ...sources: (AssetClassHints | null | undefined)[]
): AssetMarkerKind {
  for (const s of sources) {
    for (const v of [s?.asset_type, s?.vehicle_class, s?.vehicleClass]) {
      if (v && /loader/i.test(String(v))) return 'loader';
    }
  }
  return 'truck';
}

export const ASSET_MARKER_HALO = '#ffffff';
export const ASSET_MARKER_OUTLINE = '#0f172a';
export const ASSET_MARKER_DEFAULT_COLOR = '#64748b';

/** Which way the pictogram faces. */
export type AssetMarkerFacing = 'left' | 'right';

// ── Colour maths (hex only; anything else falls back to the default colour) ──────────────────

/** '#rgb' / '#rrggbb' → '#rrggbb' (lower case), else null. */
export function normaliseHex(color: string | null | undefined): string | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(color ?? '').trim());
  if (!m) return null;
  const h = m[1]!.toLowerCase();
  return h.length === 3 ? `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}` : `#${h}`;
}

const rgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];
const toHex = (c: number[]) =>
  `#${c
    .map((v) =>
      Math.round(Math.max(0, Math.min(255, v)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
/** Linear mix of two hex colours (t = 0 → a, 1 → b). */
export const mixHex = (a: string, b: string, t: number): string => {
  const x = rgb(a);
  const y = rgb(b);
  return toHex(x.map((v, i) => v + (y[i]! - v) * t));
};
const luminance = (hex: string) => {
  const [r, g, b] = rgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
/** WCAG contrast ratio of two hex colours. */
export const contrastRatio = (a: string, b: string): number => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
};
/** The pictogram colour on a state colour: white where it holds 3:1 (WCAG non-text), else ink. */
export const assetMarkerInk = (color: string): string =>
  contrastRatio(ASSET_MARKER_HALO, color) >= 3 ? ASSET_MARKER_HALO : ASSET_MARKER_OUTLINE;
/** A shade of the state colour that holds ≥ 3.4:1 on white — the hollow (stale) outline. */
export function assetMarkerDeep(color: string): string {
  let c = color;
  for (let t = 0.05; contrastRatio(c, ASSET_MARKER_HALO) < 3.4 && t < 0.95; t += 0.05) {
    c = mixHex(color, ASSET_MARKER_OUTLINE, t);
  }
  return c;
}

/** Degrees clockwise from north, normalised to [0, 360); null when not a finite number. */
export const normaliseHeading = (deg: number | null | undefined): number | null =>
  typeof deg === 'number' && Number.isFinite(deg) ? ((deg % 360) + 360) % 360 : null;

/** The pictogram faces left while travelling west (heading in (180°, 360°)), else right. */
export const assetMarkerFacing = (
  heading: number | null | undefined,
  moving: boolean
): AssetMarkerFacing => {
  const h = normaliseHeading(heading);
  return moving && h !== null && h > 180 ? 'left' : 'right';
};

// ── Geometry (48 × 48 box, centred on the position) ──────────────────────────────────────────
export const ASSET_MARKER_VIEWBOX = 48;
const C = 24;
const DISC_R = 14.2; // the coloured disc
const RING_R = 15.9; // its white keyline (outer edge)
// Disc + pointed nose, nose up (heading 0 = north); turned about the centre.
const PUCK = 'M24 2.2L34.77 14.75A14.2 14.2 0 1 1 13.23 14.75Z';

// Upright side-view pictograms in a 24 × 24 box, facing right; visual centre (12, 12.6).
interface Glyph {
  solid: string[];
  /** Cut-outs drawn in the disc colour (windows). */
  knock: string[];
  wheels: [number, number, number][];
  hub: number;
}
const GLYPHS: Record<AssetMarkerKind, Glyph> = {
  truck: {
    solid: [
      'M1.6 6.4Q1.6 5.2 2.8 5.2H13.4V15H1.6Z', // cargo body
      'M14.4 8.2H19Q19.9 8.2 20.4 9L22.4 12.1Q22.8 12.7 22.8 13.4V15H14.4Z', // cab
      'M1.6 15H22.8V16.9H1.6Z', // chassis
    ],
    knock: ['M15.8 9.6H18.7L20.6 12.6H15.8Z'],
    wheels: [
      [5.2, 17.9, 2.4],
      [10.2, 17.9, 2.4],
      [18.6, 17.9, 2.4],
    ],
    hub: 0.8,
  },
  loader: {
    solid: [
      'M1.4 11.4Q1.4 10.2 2.6 10.2H7.4V17H1.4Z', // engine / counterweight
      'M6.4 3.4H12.4Q13.2 3.4 13.2 4.2V11H6.4Z', // cab
      'M6.4 11H15.6V17H6.4Z', // front frame
      'M12.2 8.2L20.4 12.2L19.2 14.6L11 10.6Z', // lift arm
      'M17.4 10.8H20.8Q21.6 10.8 21.9 11.6L23.6 19.6H17.4Z', // bucket
    ],
    knock: ['M7.8 4.8H11.8V9H7.8Z'],
    wheels: [
      [4.6, 18.2, 3.5],
      [13.4, 18.2, 3.5],
    ],
    hub: 1.1,
  },
};
const GLYPH_SCALE = 0.88;
const GLYPH_CY = 24.4;

// ── A tiny element tree, rendered either as React elements or as markup ──────────────────────
export interface SvgNode {
  tag: string;
  attrs: Record<string, string | number>;
  children?: SvgNode[];
}
const el = (
  tag: string,
  attrs: Record<string, string | number>,
  children?: SvgNode[]
): SvgNode => ({
  tag,
  attrs,
  ...(children ? { children } : {}),
});

const escAttr = (v: string | number) =>
  String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

function toMarkup(n: SvgNode): string {
  const attrs = Object.entries(n.attrs)
    .map(([k, v]) => ` ${k}="${escAttr(v)}"`)
    .join('');
  const kids = (n.children ?? []).map(toMarkup).join('');
  return kids ? `<${n.tag}${attrs}>${kids}</${n.tag}>` : `<${n.tag}${attrs}/>`;
}

// SVG attribute → React prop (stroke-width → strokeWidth); data-* stays as written.
const camel = (k: string) =>
  k.startsWith('data-') ? k : k.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
function toReact(n: SvgNode, key: string | number): ReactElement {
  const Tag = n.tag as unknown as 'g';
  const props: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(n.attrs)) props[camel(k)] = v;
  return (
    <Tag key={key} {...(props as Record<string, string>)}>
      {(n.children ?? []).map((c, i) => toReact(c, i))}
    </Tag>
  );
}

// ── Parts ────────────────────────────────────────────────────────────────────────────────────
interface Paint {
  /** The state colour (#rrggbb). */
  color: string;
  kind: AssetMarkerKind;
  hollow: boolean;
  /** Draw the nose (the asset is moving and has a heading). */
  nose: boolean;
  facing: AssetMarkerFacing;
  /** Prefix for gradient ids (unique per inline SVG in a page). */
  idp: string;
}

/** Gradient defs for the face: a top light on the disc and a soft highlight. */
function defsNodes(p: Paint): SvgNode {
  return el('defs', {}, [
    el('linearGradient', { id: `${p.idp}g`, x1: 0, y1: 0, x2: 0, y2: 1 }, [
      el('stop', { offset: 0, 'stop-color': mixHex(p.color, ASSET_MARKER_HALO, 0.2) }),
      el('stop', { offset: 0.55, 'stop-color': p.color }),
      el('stop', { offset: 1, 'stop-color': mixHex(p.color, ASSET_MARKER_OUTLINE, 0.14) }),
    ]),
    el('radialGradient', { id: `${p.idp}h`, cx: 0.5, cy: 0.18, r: 0.7 }, [
      el('stop', { offset: 0, 'stop-color': ASSET_MARKER_HALO, 'stop-opacity': 0.34 }),
      el('stop', { offset: 0.6, 'stop-color': ASSET_MARKER_HALO, 'stop-opacity': 0 }),
    ]),
  ]);
}

/** The nose: white-keylined puck in the state colour, nose up; `rotate` turns it. */
function noseNodes(p: Paint, rotate: number): SvgNode {
  return el('g', { 'data-part': 'nose', transform: `rotate(${rotate} ${C} ${C})` }, [
    el('path', {
      d: PUCK,
      fill: ASSET_MARKER_HALO,
      stroke: ASSET_MARKER_HALO,
      'stroke-width': 3.4,
      'stroke-linejoin': 'round',
    }),
    el('path', {
      d: PUCK,
      fill: p.color,
      stroke: p.color,
      'stroke-width': 0.9,
      'stroke-linejoin': 'round',
    }),
    el('path', {
      d: 'M24 5.6L31.6 14.4',
      fill: 'none',
      stroke: ASSET_MARKER_HALO,
      'stroke-opacity': 0.35,
      'stroke-width': 0.9,
      'stroke-linecap': 'round',
    }),
  ]);
}

/** The pictogram, upright, in `ink` with cut-outs in `knock`. */
function glyphNodes(p: Paint, ink: string, knock: string): SvgNode {
  const g = GLYPHS[p.kind];
  const sx = p.facing === 'left' ? -GLYPH_SCALE : GLYPH_SCALE;
  return el(
    'g',
    {
      'data-part': 'pictogram',
      transform: `translate(${C} ${GLYPH_CY}) scale(${sx} ${GLYPH_SCALE}) translate(-12 -12.6)`,
    },
    [
      el(
        'g',
        { fill: ink },
        g.solid.map((d) => el('path', { d }))
      ),
      el(
        'g',
        { fill: knock },
        g.knock.map((d) => el('path', { d }))
      ),
      ...g.wheels.flatMap(([cx, cy, r]) => [
        el('circle', {
          cx,
          cy,
          r,
          fill: ink,
          stroke: knock,
          'stroke-width': 1.3,
          'paint-order': 'stroke',
        }),
        el('circle', { cx, cy, r: g.hub, fill: knock }),
      ]),
    ]
  );
}

/** The face: the disc (with its keyline when there is no nose) and the upright pictogram. */
function faceNodes(p: Paint): SvgNode {
  if (p.hollow) {
    const deep = assetMarkerDeep(p.color);
    return el('g', { 'data-part': 'face' }, [
      el('circle', { cx: C, cy: C, r: RING_R, fill: ASSET_MARKER_HALO }),
      el('circle', {
        cx: C,
        cy: C,
        r: DISC_R - 0.8,
        fill: ASSET_MARKER_HALO,
        stroke: deep,
        'stroke-width': 2,
        'stroke-dasharray': '3.4 2.2',
      }),
      glyphNodes(p, deep, ASSET_MARKER_HALO),
    ]);
  }
  return el('g', { 'data-part': 'face' }, [
    // With a nose, the nose's own keyline rings the disc; a ring here would cut across it.
    ...(p.nose ? [] : [el('circle', { cx: C, cy: C, r: RING_R, fill: ASSET_MARKER_HALO })]),
    el('circle', { cx: C, cy: C, r: DISC_R, fill: `url(#${p.idp}g)` }),
    el('circle', { cx: C, cy: C, r: DISC_R, fill: `url(#${p.idp}h)` }),
    ...(p.nose
      ? []
      : [
          el('circle', {
            cx: C,
            cy: C,
            r: DISC_R,
            fill: 'none',
            stroke: ASSET_MARKER_OUTLINE,
            'stroke-opacity': 0.28,
            'stroke-width': 0.7,
          }),
        ]),
    glyphNodes(p, assetMarkerInk(p.color), p.color),
  ]);
}

/** A soft silhouette for a map shadow layer (offset there with `icon-translate`). */
function shadowNodes(shape: 'puck' | 'disc'): SvgNode[] {
  const silhouette = (opacity: number) =>
    shape === 'puck'
      ? el('path', {
          d: PUCK,
          fill: ASSET_MARKER_OUTLINE,
          'fill-opacity': opacity,
          stroke: ASSET_MARKER_OUTLINE,
          'stroke-opacity': opacity,
          'stroke-width': 3.4,
          'stroke-linejoin': 'round',
        })
      : el('circle', {
          cx: C,
          cy: C,
          r: RING_R,
          fill: ASSET_MARKER_OUTLINE,
          'fill-opacity': opacity,
        });
  return [
    el('defs', {}, [
      el('filter', { id: 'b1', x: '-30%', y: '-30%', width: '160%', height: '160%' }, [
        el('feGaussianBlur', { stdDeviation: 0.7 }),
      ]),
      el('filter', { id: 'b2', x: '-30%', y: '-30%', width: '160%', height: '160%' }, [
        el('feGaussianBlur', { stdDeviation: 1.8 }),
      ]),
    ]),
    el('g', { filter: 'url(#b2)' }, [silhouette(0.26)]),
    el('g', { filter: 'url(#b1)' }, [silhouette(0.42)]),
  ];
}

// Two-layer drop shadow for inline SVG (applied after the rotation, so it always falls down).
const CSS_SHADOW =
  'drop-shadow(0 0.6px 0.6px rgba(15, 23, 42, 0.45)) drop-shadow(0 1.4px 1.6px rgba(15, 23, 42, 0.26))';

interface MarkerArgs {
  kind?: AssetMarkerKind;
  color?: string;
  heading?: number | null;
  /** Show the nose. Defaults to "has a heading". */
  moving?: boolean;
  hollow?: boolean;
}

/** Whether the marker draws its nose: not hollow, moving (default: has a heading), heading known. */
export const assetMarkerHasNose = ({
  heading,
  moving,
  hollow = false,
}: Pick<MarkerArgs, 'heading' | 'moving' | 'hollow'>): boolean => {
  const h = normaliseHeading(heading);
  return !hollow && (moving ?? h !== null) && h !== null;
};

function resolve(
  { kind = 'truck', color, heading = null, moving, hollow = false }: MarkerArgs,
  idp: string
) {
  const hex = normaliseHex(color) ?? ASSET_MARKER_DEFAULT_COLOR;
  const h = normaliseHeading(heading);
  const nose = assetMarkerHasNose({ heading, moving, hollow });
  const paint: Paint = { color: hex, kind, hollow, nose, facing: assetMarkerFacing(h, nose), idp };
  return { paint, heading: h };
}

// ── <AssetMarkerIcon> ────────────────────────────────────────────────────────────────────────
export interface AssetMarkerIconProps {
  kind?: AssetMarkerKind;
  /** The asset's status key (map/assetStatus): its colour, and hollow for "offline". */
  status?: AssetStatusKey;
  /** State colour (#rgb or #rrggbb); `status` wins when both are given. */
  color?: string;
  /** Degrees clockwise from north; null/undefined: no heading (no nose). */
  heading?: number | null;
  /** Draw the nose. Defaults to "has a heading"; pass false for a stationary asset. */
  moving?: boolean;
  /** Rendered size in px (square; the box includes the nose). */
  size?: number;
  /** Hollow dashed disc: no live fix at this time. */
  hollow?: boolean;
  /** Accessible name; omit when the surrounding element names it. */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

/** The asset marker as inline SVG: a disc with an upright pictogram, nose on the heading. */
export function AssetMarkerIcon({
  kind = 'truck',
  status,
  color: colorProp = ASSET_MARKER_DEFAULT_COLOR,
  heading = null,
  moving,
  size = 32,
  hollow: hollowProp = false,
  label,
  className,
  style,
}: AssetMarkerIconProps) {
  const idp = `am${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const color = status ? ASSET_STATUS[status].marker : colorProp;
  const hollow = hollowProp || (status ? !!ASSET_STATUS[status].hollow : false);
  const { paint, heading: h } = resolve({ kind, color, heading, moving, hollow }, idp);
  const nodes: SvgNode[] = [
    ...(paint.hollow ? [] : [defsNodes(paint)]),
    ...(paint.nose ? [noseNodes(paint, h ?? 0)] : []),
    faceNodes(paint),
  ];
  return (
    <svg
      viewBox={`0 0 ${ASSET_MARKER_VIEWBOX} ${ASSET_MARKER_VIEWBOX}`}
      width={size}
      height={size}
      className={className}
      style={{ display: 'block', overflow: 'visible', filter: CSS_SHADOW, ...style }}
      shapeRendering="geometricPrecision"
      data-kind={kind}
      data-status={status}
      data-heading={Math.round(h ?? 0)}
      data-moving={paint.nose ? 'true' : undefined}
      data-hollow={hollow ? 'true' : undefined}
      data-facing={paint.facing}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      {nodes.map((n, i) => toReact(n, i))}
    </svg>
  );
}

// ── Standalone markup ────────────────────────────────────────────────────────────────────────
/** `full` is the whole icon at its heading; the rest are the map's symbol-layer parts. */
export type AssetMarkerPart = 'full' | 'nose' | 'face' | 'shadow';

export interface AssetMarkerSvgOptions extends MarkerArgs {
  part?: AssetMarkerPart;
  /** Rendered width and height in px. */
  size?: number;
  /** Face only: override the facing derived from heading + moving. */
  facing?: AssetMarkerFacing;
}

/**
 * The marker (or one map part of it) as standalone SVG markup. Map parts are drawn nose up and
 * unshadowed: maplibre turns the nose with `icon-rotate` and offsets the shadow with
 * `icon-translate` (see map/assetMarkerImages).
 */
export function assetMarkerSvg({
  part = 'full',
  size = 48,
  facing,
  ...args
}: AssetMarkerSvgOptions = {}): string {
  const { paint, heading } = resolve(args, '');
  if (facing) paint.facing = facing;
  let body: SvgNode[];
  switch (part) {
    case 'nose':
      body = [noseNodes({ ...paint, nose: true }, 0)];
      break;
    case 'face':
      body = [...(paint.hollow ? [] : [defsNodes(paint)]), faceNodes(paint)];
      break;
    case 'shadow':
      body = shadowNodes(paint.nose ? 'puck' : 'disc');
      break;
    default:
      body = [
        el('defs', {}, [
          el(
            'filter',
            {
              id: 's',
              x: '-40%',
              y: '-40%',
              width: '180%',
              height: '180%',
              'color-interpolation-filters': 'sRGB',
            },
            [
              el('feDropShadow', {
                dx: 0,
                dy: 0.7,
                stdDeviation: 0.6,
                'flood-color': ASSET_MARKER_OUTLINE,
                'flood-opacity': 0.45,
                result: 'a',
              }),
              el('feDropShadow', {
                in: 'a',
                dx: 0,
                dy: 1.8,
                stdDeviation: 1.7,
                'flood-color': ASSET_MARKER_OUTLINE,
                'flood-opacity': 0.26,
              }),
            ]
          ),
        ]),
        ...(paint.hollow ? [] : [defsNodes(paint)]),
        el('g', { filter: 'url(#s)' }, [
          ...(paint.nose ? [noseNodes(paint, heading ?? 0)] : []),
          faceNodes(paint),
        ]),
      ];
  }
  return toMarkup(
    el(
      'svg',
      {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: `0 0 ${ASSET_MARKER_VIEWBOX} ${ASSET_MARKER_VIEWBOX}`,
        width: size,
        height: size,
      },
      body
    )
  );
}
